import { Injectable } from '@angular/core';
import { MEDIA_BREAKPOINTS } from '../../constants';

@Injectable({
  providedIn: 'root',
})
export class PostMessageService {
  readonly fixedElementTypes = [
    '[data-iframe-fixed-top]',
    '[data-iframe-fixed-bottom]',
  ];
  readonly stickyElementTypes = [
    '[data-iframe-sticky-top]',
    '[data-iframe-sticky-bottom]',
  ];
  readonly fixedHeightElementTypes = [
    '[data-iframe-fixed-height-gap]',
    '[data-iframe-md-fixed-height-gap]',
    '[data-iframe-lg-fixed-height-gap]',
  ];
  readonly parentChannel = 'vinajet';
  readonly channel = 'plugin-vinajet';
  readonly minIframeHeight = 1077;

  /**
   * Posts a message to the parent window to scroll the iframe into view.
   */
  postMessageScrollIntoView() {
    window.parent.postMessage(
      {
        channel: this.parentChannel,
        scrollIntoView: true,
      },
      {
        targetOrigin: '*',
      },
    );
  }

  postMessageSizeChange(iframeWidth: number, iframeHeight: number) {
    window.parent.postMessage(
      {
        channel: this.parentChannel,
        iframeWidth: iframeWidth,
        iframeHeight: Math.max(this.minIframeHeight, iframeHeight),
      },
      {
        targetOrigin: '*',
      },
    );
  }

  postMessageUpdateTranIdInQueryParams(tranId: string | null) {
    window.parent.postMessage(
      {
        channel: this.parentChannel,
        tranId: tranId,
      },
      {
        targetOrigin: '*',
      },
    );
  }

  registerPostMessageOnSizeChange() {
    // Send the initial message for first load
    this.postMessageSizeChange(window.innerWidth, document.body.scrollHeight);

    // Observe the resize event
    const observer = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        const { width, height } = entry.contentRect;
        this.postMessageSizeChange(width, height);
      });
    });
    observer.observe(document.documentElement);
  }

  listenPluginMessage() {
    window.addEventListener('message', (event: MessageEvent<any>) => {
      if (event.data && event.data.channel === this.channel) {
        const data = event.data;
        const {
          iframeBounding,
          parentInnerHeight,
        }: { iframeBounding: DOMRect; parentInnerHeight: number } = data;
        requestAnimationFrame(() => {
          this.recalculateFixedElements(iframeBounding, parentInnerHeight);
          this.recalculateStickyElements(iframeBounding, parentInnerHeight);
          this.recalculateFixedHeightElements(parentInnerHeight);
        });
      }
    });
  }

  /**
   * Recalculate the position of elements with data-iframe-fixed-* value
   * based on the current scroll position and the bounding of the iframe.
   * THIS APPLY FOR FIXED ELEMENTS
   * @param {DOMRect} iframeBounding The bounding rectangle of the iframe in parent DOM.
   * @param {number} parentInnerHeight The inner height of the parent element embed the iframe.
   * @returns {void}
   */
  private recalculateFixedElements(
    iframeBounding: DOMRect,
    parentInnerHeight: number,
  ): void {
    const fixedElements = document.querySelectorAll<HTMLElement>(
      this.fixedElementTypes.join(','),
    );

    // From top iframe to top of viewport parent (maximize bottom iframe)
    const calculatedTopView = Math.max(
      0,
      Math.min(iframeBounding.top * -1, iframeBounding.height),
    );

    // From top iframe to bottom of viewport parent (maximize bottom iframe)
    const calculatedBottomView = Math.max(
      0,
      Math.min(parentInnerHeight - iframeBounding.top, iframeBounding.height),
    );

    fixedElements.forEach((element) => {
      const topValue = element.dataset['iframeFixedTop'];
      const bottomValue = element.dataset['iframeFixedBottom'];
      element.style.top = 'unset';
      element.style.bottom = 'unset';

      if (topValue !== undefined) {
        const top = parseInt(topValue);
        element.style.top = `${
          calculatedTopView + top + element.offsetHeight < calculatedBottomView
            ? calculatedTopView + top
            : Math.max(calculatedBottomView - element.offsetHeight, top)
        }px`;
      }

      if (bottomValue !== undefined) {
        const bottom = parseInt(bottomValue);
        // element.style.top = `${Math.max(
        //   0,
        //   calculatedBottomView - bottom - element.offsetHeight,
        // )}px`;

        element.style.bottom = `${Math.max(Math.min(window.innerHeight + window.scrollY - calculatedBottomView, window.innerHeight - element.offsetHeight), bottom)}px`;
      }
    });
  }

  /**
   * Recalculate the position of elements with data-iframe-sticky-* value relative with parent based on the current scroll position
   * and the bounding of the iframe.
   * THIS APPLY FOR STICKY/ABSOLUTE ELEMENTS
   * @param {DOMRect} iframeBounding The bounding rectangle of the iframe in parent DOM.
   * @param {number} parentInnerHeight The inner height of the parent element embed the iframe.
   * @returns {void}
   */
  recalculateStickyElements(
    iframeBounding: DOMRect,
    parentInnerHeight: number,
  ): void {
    const stickyElements = document.querySelectorAll<HTMLElement>(
      this.stickyElementTypes.join(','),
    );

    // From top iframe to top of viewport parent (maximize bottom iframe)
    const calculatedTopView = Math.max(
      0,
      Math.min(iframeBounding.top * -1, iframeBounding.height),
    );

    // From top iframe to bottom of viewport parent (maximize bottom iframe)
    const calculatedBottomView = Math.max(
      0,
      Math.min(parentInnerHeight - iframeBounding.top, iframeBounding.height),
    );

    stickyElements.forEach((element) => {
      const topValue = element.dataset['iframeStickyTop'];
      const bottomValue = element.dataset['iframeStickyBottom'];
      element.style.top = 'unset';
      element.style.bottom = 'unset';

      if (topValue !== undefined) {
        const top = parseInt(topValue);
        const parentTop =
          (element.offsetParent?.getBoundingClientRect().top ?? 0) +
          window.scrollY;
        const topView =
          calculatedTopView < parentTop ? 0 : calculatedTopView - parentTop;

        element.style.top = `${topView + top}px`;
      }

      if (bottomValue !== undefined) {
        const bottom = parseInt(bottomValue);
        const parentHeight =
          element.offsetParent?.getBoundingClientRect().height ?? 0;
        const parentBottom =
          (element.offsetParent?.getBoundingClientRect().bottom ?? 0) +
          window.scrollY;

        element.style.bottom = `${Math.max(Math.min(parentBottom - calculatedBottomView, parentHeight - element.offsetHeight), bottom)}px`;
      }
    });
  }

  /**
   * Recalculates the fixed height elements by setting their max-height CSS property.
   * It uses the data attributes iframeFixedHeightGap, iframeMdFixedHeightGap, iframeLgFixedHeightGap
   * to determine the gap top + bottom of the element with responsive mobile-first style.
   * If the gap is negative, it resets the max-height to unset.
   *
   * @param parentInnerHeight the inner height of the parent element embed the iframe.
   */
  private recalculateFixedHeightElements(parentInnerHeight: number): void {
    const fixedHeightElements = document.querySelectorAll<HTMLElement>(
      this.fixedHeightElementTypes.join(','),
    );
    fixedHeightElements.forEach((element) => {
      const gap = parseInt(element.dataset['iframeFixedHeightGap'] ?? '');
      if (!isNaN(gap)) {
        if (gap < 0) {
          element.style.maxHeight = `unset`;
        } else {
          element.style.maxHeight = `${parentInnerHeight - gap}px`;
        }
      }

      const mdGap = parseInt(element.dataset['iframeMdFixedHeightGap'] ?? '');
      if (!isNaN(mdGap) && window.innerWidth >= MEDIA_BREAKPOINTS.md) {
        element.style.maxHeight = `${parentInnerHeight - mdGap}px`;
      }

      const lgGap = parseInt(element.dataset['iframeLgFixedHeightGap'] ?? '');
      if (!isNaN(lgGap) && window.innerWidth >= MEDIA_BREAKPOINTS.lg) {
        element.style.maxHeight = `${parentInnerHeight - lgGap}px`;
      }
    });
  }
}
